import React, { useState, useEffect } from 'react'; 
import { useNavigate } from 'react-router-dom';
import { db } from '../firebase';
import { collection, getDocs, addDoc } from 'firebase/firestore';
import { loadUserProgress, getLevel } from './progressManager';
import { 
  Search, Plus, X, ChevronDown, ChevronUp, Target, 
  BookOpen, School, Sparkles, Loader2
} from 'lucide-react';

const CompetencesList = ({ userId }) => {
  const [competences, setCompetences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [progressData, setProgressData] = useState({});
  const [newCompetence, setNewCompetence] = useState({
    title: '',
    theme: '',
    chapitre: '',
    classe: '',
  });
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [expandedChapter, setExpandedChapter] = useState(null);
  const [chaptersSuggestions, setChaptersSuggestions] = useState([]);
  const [isChapitreFocused, setIsChapitreFocused] = useState(false);
  
  const navigate = useNavigate();

  const themes = ['Educatif', 'Professionnel', 'Personnel', 'Business', 'Placement financier'];

  useEffect(() => {
    const fetchCompetences = async () => {
      if (!userId) {
        setError("Utilisateur non défini.");
        setLoading(false);
        return;
      }

      try {
        const competencesCollection = collection(db, 'users', userId, 'competences');
        const competencesSnapshot = await getDocs(competencesCollection);
        const competencesList = competencesSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
        }));
        setCompetences(competencesList);

        const userProgress = await loadUserProgress(userId);
        setProgressData(userProgress);
      } catch (error) {
        setError("Erreur lors de la récupération des compétences : " + error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchCompetences();
  }, [userId]);

  const handleSelectCompetence = (competence) => {
    navigate(`/Recommandations/${competence.id}`, { state: { competence } });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewCompetence(prev => ({ ...prev, [name]: value }));

    if (name === 'title') {
      const suggestedChapter = getSuggestedChapter(value);
      setNewCompetence(prev => ({ ...prev, chapitre: suggestedChapter }));
      if (isChapitreFocused) {
        filterChaptersSuggestions(value);
      }
    }
  };

  const handleAddCompetence = async () => {
    if (!newCompetence.title || !newCompetence.theme || !newCompetence.chapitre || !newCompetence.classe) {
      alert("Veuillez remplir tous les champs.");
      return;
    }

    if (!userId) {
      alert("Utilisateur non défini pour ajouter une compétence.");
      return;
    }

    setAdding(true);
    try {
      const docRef = await addDoc(collection(db, 'users', userId, 'competences'), newCompetence);
      setCompetences(prev => [...prev, { ...newCompetence, id: docRef.id }]);
      setNewCompetence({ title: '', theme: '', chapitre: '', classe: '' });
      setShowForm(false);
    } catch (error) {
      console.error("Erreur lors de l'ajout de la compétence : ", error);
    } finally {
      setAdding(false);
    }
  };

  const groupByChapter = (competences) => {
    return competences.reduce((chapters, competence) => {
      const chapter = competence.chapitre || 'Autres objectifs';
      if (!chapters[chapter]) chapters[chapter] = [];
      chapters[chapter].push(competence);
      return chapters;
    }, {});
  };

  const getSuggestedChapter = (title) => {
    if (title) {
      const similarChapter = competences.filter(competence => 
        competence.title.toLowerCase().includes(title.toLowerCase())
      ).map(competence => competence.chapitre);
      
      return similarChapter.length > 0 ? similarChapter[0] : '';
    }
    return '';
  };

  const filterChaptersSuggestions = (title) => {
    const filteredChapters = competences
      .map(competence => competence.chapitre)
      .filter((chapter, index, self) => 
        self.indexOf(chapter) === index && 
        chapter.toLowerCase().includes(title.toLowerCase())
      );

    setChaptersSuggestions(filteredChapters);
  };

  const filteredCompetences = competences.filter(competence => {
    const searchLower = searchTerm.toLowerCase();
    return (
      (competence.title || '').toLowerCase().includes(searchLower) ||
      (competence.theme || '').toLowerCase().includes(searchLower)
    );
  });

  const groupedCompetences = groupByChapter(filteredCompetences);
  const allChapters = [...new Set(competences.map(competence => competence.chapitre))];

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="w-full max-w-6xl mx-auto px-4 py-8 space-y-8">
      <div className="text-center mb-12">
        <h1 className="text-3xl font-bold text-blue-800 mb-4 flex items-center justify-center gap-3">
          <Sparkles className="h-8 w-8 text-blue-600" />
          Développez vos compétences & pilotez vos objectifs
        </h1>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-600 rounded-lg p-4">
          {error}
        </div>
      )}

      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
        <input
          type="text"
          placeholder="Rechercher un objectif ou une compétence..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-10 pr-4 py-3 rounded-lg border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>

      <div className="space-y-4">
        {Object.entries(groupedCompetences).map(([chapter, chapterCompetences]) => (
          <div key={chapter} className="bg-white rounded-xl shadow-lg overflow-hidden">
            <button
              onClick={() => setExpandedChapter(expandedChapter === chapter ? null : chapter)}
              className="w-full flex items-center justify-between p-4 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 text-white rounded-t-xl"
            >
              <div className="flex items-center gap-2">
                <BookOpen className="h-5 w-5" />
                <span className="font-semibold text-lg">{chapter}</span>
              </div>
              {expandedChapter === chapter ? (
                <ChevronUp className="h-5 w-5" />
              ) : (
                <ChevronDown className="h-5 w-5" />
              )}
            </button>

            {expandedChapter === chapter && (
              <div className="divide-y divide-gray-100">
                {chapterCompetences.map((competence) => {
                  const progress = progressData[competence.id]?.progress || 0;
                  const level = getLevel(progress);
                  
                  return (
                    <div
                      key={competence.id}
                      onClick={() => handleSelectCompetence(competence)}
                      className="p-4 hover:bg-gray-50 cursor-pointer transition-colors"
                    >
                      <div className="space-y-3">
                        <div className="flex items-start justify-between">
                          <div className="space-y-1">
                            <h3 className="font-medium text-lg text-gray-900 flex items-center gap-2">
                              <Target className="h-5 w-5 text-blue-600" />
                              {competence.title}
                            </h3>
                            <p className="text-sm text-gray-500 flex items-center gap-2">
                              <School className="h-4 w-4" />
                              {competence.theme} · {competence.classe}
                            </p>
                          </div>
                        </div>

                        <div className="space-y-1">
                          <div className="flex justify-between text-sm text-gray-600">
                            <span>Progression</span>
                            <span>{progress}% - Niveau {level}</span>
                          </div>
                          <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 transition-all duration-500"
                              style={{ width: `${progress}%` }}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="flex justify-center mt-8">
        <button
          onClick={() => setShowForm(!showForm)}
          className="bg-gradient-to-r from-blue-100 to-blue-200 text-blue-800 px-6 py-3 rounded-lg shadow hover:shadow-md transition-all flex items-center gap-2"
        >
          {showForm ? (
            <>
              <X className="h-5 w-5" />
              Fermer
            </>
          ) : (
            <>
              <Plus className="h-5 w-5" />
              Ajouter un nouvel objectif
            </>
          )}
        </button>
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-900">Créer un nouvel objectif</h2>
              <button onClick={() => setShowForm(false)} className="text-gray-400 hover:text-gray-600">
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-4">
              <input
                type="text"
                placeholder="Titre de l'objectif"
                name="title"
                value={newCompetence.title}
                onChange={handleInputChange}
                className="w-full p-3 rounded-lg border border-gray-200 focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white hover:bg-gray-50 transition-colors"
              />
              
              <select
                name="theme"
                value={newCompetence.theme}
                onChange={handleInputChange}
                className="w-full p-3 rounded-lg border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Choisir un thème</option>
                {themes.map((theme, index) => (
                  <option key={index} value={theme}>{theme}</option>
                ))}
              </select>

              <div className="relative">
                <input
                  type="text"
                  placeholder="Chapitre"
                  name="chapitre"
                  value={newCompetence.chapitre}
                  onChange={handleInputChange}
                  onFocus={() => setIsChapitreFocused(true)} // Montrer la liste des chapitres lorsque le champ est focus
                  onBlur={() => setIsChapitreFocused(false)} // Masquer la liste des chapitres lorsque le champ perd le focus
                  className="w-full p-3 rounded-lg border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                {isChapitreFocused && chaptersSuggestions.length > 0 && (
                  <ul className="absolute bg-white shadow-lg rounded-lg w-full mt-1 max-h-48 overflow-y-auto z-20 border border-gray-200">
                    {chaptersSuggestions.map((chapter, index) => (
                      <li
                        key={index}
                        onClick={() => {
                          setNewCompetence(prev => ({ ...prev, chapitre: chapter }));
                          setChaptersSuggestions([]);
                        }}
                        className="p-2 cursor-pointer hover:bg-gray-100"
                      >
                        {chapter}
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <input
                type="text"
                placeholder="Niveau de classe"
                name="classe"
                value={newCompetence.classe}
                onChange={handleInputChange}
                className="w-full p-3 rounded-lg border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />

              <button
                onClick={handleAddCompetence}
                disabled={adding}
                className={`w-full p-3 rounded-lg text-white font-medium flex items-center justify-center gap-2
                  ${adding 
                    ? 'bg-gray-200 text-gray-500 cursor-not-allowed' 
                    : 'bg-gradient-to-r from-blue-100 to-blue-200 text-blue-800 hover:from-blue-200 hover:to-blue-300'
                  }`}
              >
                {adding ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin" />
                    Création en cours...
                  </>
                ) : (
                  <>
                    <Plus className="h-5 w-5" />
                    Créer l'objectif
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompetencesList;
